import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { sendLead } from "@/lib/send-lead.functions";

type Step = {
  question: string;
  description: string;
  options?: string[];
  fields?: { label: string; key: string; placeholder: string }[];
};

const STEPS: Step[] = [
  {
    question: "Qual é o seu faturamento mensal atual?",
    description: "Selecione a opção que melhor descreve sua situação.",
    options: [
      "Até R$10 mil",
      "R$10 mil a R$30 mil",
      "R$30 mil a R$100 mil",
      "R$100 mil a R$300 mil",
      "Acima de R$300 mil",
    ],
  },
  {
    question: "Hoje, qual é o maior desafio da sua empresa?",
    description: "Conte um pouco sobre o que mais te preocupa.",
    options: [
      "Atrair mais clientes",
      "Vender mais com tráfego pago",
      "Melhorar os criativos/anúncios",
      "Ter uma marca mais forte",
      "Converter melhor no site ou landing page",
      "Organizar estratégia, dados e acompanhamento",
    ],
  },
  {
    question: "Qual área você acredita que mais trava o crescimento da sua marca hoje?",
    description: "O que mais te trava no momento?",
    options: [
      "Tráfego",
      "Criativos",
      "Posicionamento",
      "Site/página de vendas",
      "Atendimento/comercial",
      "Não sei exatamente, preciso de um diagnóstico",
    ],
  },
  {
    question: "Quais são seus dados para nossa equipe te chamar?",
    description: "Preencha seus dados de contato.",
    fields: [
      { label: "Nome", key: "nome", placeholder: "Seu nome completo" },
      { label: "WhatsApp", key: "whatsapp", placeholder: "(00) 00000-0000" },
      { label: "Instagram da empresa", key: "instagram", placeholder: "@suaempresa" },
      { label: "Nome da empresa", key: "empresa", placeholder: "Nome da sua empresa" },
    ],
  },
];

export default function FormSection() {

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<(string | null)[]>(Array(STEPS.length).fill(null));
  const [contactData, setContactData] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const submit = useServerFn(sendLead);

  const current = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;

  const handleSelect = (option: string) => {
    const next = [...answers];
    next[step] = option;
    setAnswers(next);
    if (step < STEPS.length - 1) {
      setTimeout(() => setStep(step + 1), 200);
    }
  };

  const handleFieldChange = (key: string, value: string) => {
    setContactData((prev) => ({ ...prev, [key]: value }));
  };

  const isLastStep = step === STEPS.length - 1;
  const canSubmitLast = isLastStep && current.fields && current.fields.every((f) => contactData[f.key]?.trim());

  return (
    <section
      id="formulario"
      className="relative w-full bg-black px-6 py-24 font-poppins text-white md:px-12 lg:px-16"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[55%] bg-gradient-to-b from-black via-black/70 to-transparent backdrop-blur-[2px] [mask-image:linear-gradient(to_bottom,black,transparent)]"
      />


      <div className="relative z-[2] mx-auto grid max-w-7xl grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
        {/* Left: copy + image */}
        <div className="flex flex-col justify-center lg:pt-16">
          <span className="faq-tag mb-6">Pare de perder tempo</span>
          <h2 className="text-6xl leading-[1.05] tracking-tight text-white md:text-7xl lg:text-8xl">
            <span className="font-normal text-white/85">Preencha o</span>
            <br />
            <span className="font-bold text-white">Formulário</span>
          </h2>

          <div className="relative z-[30] mt-12 overflow-hidden rounded-2xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
            <img
              src="https://i.postimg.cc/wT9psmpd/branco-porsche-(1).webp"
              alt="Chegou a hora de escalar"
              className="aspect-[16/10] w-full object-cover"
              loading="eager"
              decoding="async"
            />
          </div>
        </div>

        {/* Right: form card */}
        <div className="flex flex-col">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_20px_60px_rgba(0,0,0,0.5)] backdrop-blur-xl backdrop-saturate-150 md:p-8">
            {/* Progress */}
            <div className="mb-2 flex items-center justify-between">
              <div className="h-1 w-32 overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-white transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs text-white/50">
                Passo {step + 1} de {STEPS.length}
              </span>
            </div>

            {/* Question card */}
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-6 backdrop-blur-md">
              <h3 className="text-lg font-semibold text-white">{current.question}</h3>
              <p className="mt-1 text-sm text-white/60">{current.description}</p>

              {current.options && (
                <div className="mt-5 flex flex-col gap-3">
                  {current.options.map((option) => {
                    const selected = answers[step] === option;
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className={`w-full rounded-full border px-5 py-4 text-left text-sm font-semibold transition-all duration-300 ${
                          selected
                            ? "border-white/40 bg-white/10 text-white shadow-[0_0_24px_rgba(255,255,255,0.15)]"
                            : "border-white/10 bg-white/[0.02] text-white/90 hover:border-white/25 hover:bg-white/[0.06]"
                        }`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              )}

              {current.fields && (
                <div className="mt-5 flex flex-col gap-4">
                  {current.fields.map((field) => (
                    <div key={field.key} className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-white/80">{field.label}</label>
                      <input
                        type="text"
                        placeholder={field.placeholder}
                        value={contactData[field.key] || ""}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        className="w-full rounded-full border border-white/10 bg-white/[0.03] px-5 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-all focus:border-white/30 focus:bg-white/[0.06]"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Nav */}
            <div className="mt-6 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStep(Math.max(0, step - 1))}
                disabled={step === 0}
                className="rounded-full border border-white/10 px-5 py-2 text-sm text-white/70 transition-all hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-30"
              >
                Voltar
              </button>
              {isLastStep && (
                <div className="flex flex-col items-end gap-2">
                  <button
                    type="button"
                    disabled={!canSubmitLast || status === "sending" || status === "success"}
                    onClick={async () => {
                      try {
                        setStatus("sending");
                        setErrorMsg(null);
                        await submit({
                          data: {
                            faturamento: answers[0] ?? "",
                            desafio: answers[1] ?? "",
                            area: answers[2] ?? "",
                            nome: contactData.nome ?? "",
                            whatsapp: contactData.whatsapp ?? "",
                            instagram: contactData.instagram ?? "",
                            empresa: contactData.empresa ?? "",
                          },
                        });
                        setStatus("success");
                      } catch (err) {
                        setStatus("error");
                        setErrorMsg(err instanceof Error ? err.message : "Erro ao enviar");
                      }
                    }}
                    className="rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-black shadow-[0_8px_24px_rgba(255,255,255,0.15)] transition-all hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    {status === "sending" ? "Enviando..." : status === "success" ? "Enviado ✓" : "Enviar"}
                  </button>
                  {status === "success" && (
                    <span className="text-xs text-emerald-400">Recebemos seus dados! Entraremos em contato.</span>
                  )}
                  {status === "error" && (
                    <span className="text-xs text-red-400">{errorMsg ?? "Erro ao enviar"}</span>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

    </section>
  );
}
